import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { DrawerModule } from 'primeng/drawer';
import { ToastModule } from 'primeng/toast';
import { fromEvent } from 'rxjs';
import { AppProperty } from '../../properties';
import { NotificationNavigationService } from '../../services/notification-navigation.service';
import { NotificationItem, NotificationsStore } from '../../services/notifications';

@Component({
  selector: 'alert',
  templateUrl: './alert.component.html',
  styleUrls: ['./alert.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [DatePipe, DrawerModule, ToastModule, TranslateModule],
})
export class AlertComponent implements OnInit {
  readonly store = inject(NotificationsStore);
  private readonly navigation = inject(NotificationNavigationService);
  private readonly messageService = inject(MessageService);
  private readonly appProperty = inject(AppProperty);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly visible = input(false);
  readonly message = input(false);
  readonly close = output<boolean>();
  readonly alertsCount = output<number>();
  readonly opening = signal<string | null>(null);
  readonly drawerPosition = signal<'right' | 'bottom'>(this.currentDrawerPosition());
  readonly empty = computed(() => !this.store.loading()
    && this.store.items().length === 0
    && this.store.legacyState().loaded
    && this.store.notifierState().loaded
    && !this.store.legacyState().error
    && !this.store.notifierState().error);

  constructor() {
    // The count includes every persisted V3 page, not only visible rows.
    effect(() => this.alertsCount.emit(this.store.unreadCount()));
    effect(() => {
      if (this.visible()) {
        untracked(() => this.store.panelOpened());
      }
    });
    effect(() => {
      if (!this.store.sessionActive()) {
        untracked(() => {
          this.messageService.clear('newAlert');
          this.appProperty.setNewAlertReceived(false);
        });
      }
    });
  }

  ngOnInit(): void {
    fromEvent(window, 'resize')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.drawerPosition.set(this.currentDrawerPosition()));

    this.store.toastEvents$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (!this.store.sessionActive()) {
          return;
        }
        this.appProperty.setNewAlertReceived(true);
        this.messageService.add({
          key: 'newAlert',
          severity: 'info',
          summary: event.title,
          detail: event.body,
          data: event.notification,
        });
      });
  }

  async goToContext(item: NotificationItem): Promise<void> {
    if (this.opening() || !this.store.sessionActive() || this.destroyRef.destroyed) {
      return;
    }
    this.opening.set(item.key);
    try {
      // Persist before navigation: selecting a different company can reload the shell.
      if (item.isUnread && !await this.store.markRead(item)) {
        return;
      }
      if (!this.store.sessionActive() || this.destroyRef.destroyed) {
        return;
      }
      if (await this.navigation.open(item)) {
        this.closeSidebar();
      }
    } catch {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('notifications.navigation.title'),
        detail: this.translate.instant('notifications.navigation.failed'),
      });
    } finally {
      this.opening.set(null);
    }
  }

  closeSidebar(): void {
    this.close.emit(true);
  }

  private currentDrawerPosition(): 'right' | 'bottom' {
    return window.innerWidth <= 640 ? 'bottom' : 'right';
  }
}
